import { Trans, useLingui } from '@lingui/react/macro';
import { TEMPLATE_NAME_CONFLICT_MESSAGE, isTemplateGroupNameConflict } from 'modules/manager/domain/template-name-conflict';
import { useCreateMediaManagerTemplateGroupMutation } from 'modules/manager/store/media-manager-api';
import MediaManagerTemplateGroupForm, {
  TEMPLATE_GROUP_FORM_INITIAL_VALUES,
  TemplateGroupFormValues,
} from 'modules/manager/ui/components/forms/media-manager-template-group-form';
import { useCallback, useState } from 'react';
import { UDText } from 'ud-ui/components';
import Modal from 'ud-ui/components/modal';

interface Args {
  tournamentId: number | null;
  existingNames: string[];
  createAsPreset?: boolean;
}

/** The group list refetches by the mutation's tags (IMGM-1515). */
function useCreateTemplateGroupModal({ tournamentId, existingNames, createAsPreset = false }: Args) {
  const { t } = useLingui();
  const [createGroup, { isLoading }] = useCreateMediaManagerTemplateGroupMutation();
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const open = useCallback(() => {
    setError(null);
    setIsOpen(true);
  }, []);
  const close = useCallback(() => setIsOpen(false), []);

  const onSubmit = useCallback(
    async (values: TemplateGroupFormValues) => {
      if (!createAsPreset && !tournamentId) {
        console.error('Cannot create a template group without an active organization');
        return;
      }
      setError(null);
      try {
        await createGroup({
          name: values.name.trim(),
          tournamentId: createAsPreset ? null : tournamentId,
        }).unwrap();
        close();
      } catch (err) {
        console.error('Failed to create media manager template group:', err);
        setError(
          isTemplateGroupNameConflict(err) ? t(TEMPLATE_NAME_CONFLICT_MESSAGE) : t`Не удалось создать группу, попробуйте ещё раз`,
        );
      }
    },
    [createAsPreset, tournamentId, createGroup, close, t],
  );

  return {
    open,
    close,
    modal: (
      <Modal show={isOpen} onClose={close} size="md">
        <UDText type="title-3" className="mb-5">
          {createAsPreset ? <Trans>Создать группу пресетов</Trans> : <Trans>Создать группу</Trans>}
        </UDText>
        <MediaManagerTemplateGroupForm
          initialValues={TEMPLATE_GROUP_FORM_INITIAL_VALUES}
          existingNames={existingNames}
          isLoading={isLoading}
          errorMessage={error}
          submitLabel={<Trans>Создать</Trans>}
          onSubmit={onSubmit}
        />
      </Modal>
    ),
  };
}

export default useCreateTemplateGroupModal;
